import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ShoppingBag, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import SEOHead from "@/components/SEOHead";
import { Button } from "@/components/ui/button";
import { CheckoutForm } from "@/components/checkout/CheckoutForm";
import { OrderSummary } from "@/components/checkout/OrderSummary";
import { OrderConfirmation } from "@/components/checkout/OrderConfirmation";
import { useCart } from "@/contexts/CartContext";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { trackPurchase, trackBeginCheckout } from "@/utils/gtm";
import type { CheckoutFormData, CheckoutResponse, DiscountValidation } from "@/types/order";

export default function Checkout() {
  const navigate = useNavigate();
  const { items, subtotal, clearCart, itemCount } = useCart();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [appliedDiscount, setAppliedDiscount] = useState<DiscountValidation | null>(null);
  const [orderConfirmation, setOrderConfirmation] = useState<CheckoutResponse | null>(null);

  // Redirect if cart is empty and no confirmation
  useEffect(() => {
    if (itemCount === 0 && !orderConfirmation) {
      navigate("/catalogo");
    }
  }, [itemCount, orderConfirmation, navigate]);

  // Track begin_checkout event when user enters checkout
  useEffect(() => {
    if (items.length > 0) {
      trackBeginCheckout({
        value: subtotal,
        items: items.map(item => ({
          id: item.productId,
          name: item.name,
          price: item.price,
          quantity: item.quantity,
          variant: item.variationName,
        })),
      });
    }
  }, []);

  const handleSubmit = async (formData: CheckoutFormData) => {
    setIsSubmitting(true);

    try {
      const { data, error } = await supabase.functions.invoke("process-checkout", {
        body: {
          ...formData,
          items,
          subtotal,
        },
      });

      if (error) {
        throw new Error(error.message || "Error al procesar el pedido");
      }

      if (!data.success) {
        throw new Error(data.error || "Error al procesar el pedido");
      }

      // Track purchase event
      trackPurchase({
        orderNumber: data.orderNumber,
        total: data.total,
        subtotal: subtotal,
        discount: data.discountApplied || 0,
        items: items.map(item => ({
          id: item.productId,
          name: item.name,
          price: item.price,
          quantity: item.quantity,
          variant: item.variationName,
        })),
      });

      // Success - show confirmation
      setOrderConfirmation(data);
      clearCart();

      toast({
        title: "¡Pedido realizado!",
        description: `Tu pedido ${data.orderNumber} ha sido recibido.`,
      });

    } catch (error) {
      console.error("Checkout error:", error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Error al procesar el pedido",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Show confirmation if order was successful
  if (orderConfirmation) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Header />
        <main className="flex-1 py-12">
          <div className="container px-4 mx-auto">
            <OrderConfirmation order={orderConfirmation} />
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // Empty cart state (shouldn't show due to redirect, but just in case)
  if (itemCount === 0) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Header />
        <main className="flex-1 flex items-center justify-center py-12">
          <div className="text-center">
            <ShoppingBag className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <h1 className="text-2xl font-bold mb-2">Tu carrito está vacío</h1>
            <p className="text-muted-foreground mb-6">
              Agrega productos para continuar con el checkout
            </p>
            <Link to="/catalogo">
              <Button>Ver Catálogo</Button>
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <SEOHead
        title="Checkout"
        description="Completá tu pedido de productos para mascotas. Envío a todo Costa Rica."
        noIndex={true}
      />
      <Header />
      <main className="flex-1 py-8">
        <div className="container px-4 mx-auto">
          {/* Back Link */}
          <Link
            to="/catalogo"
            className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-6"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver al catálogo
          </Link>

          <h1 className="text-3xl font-bold mb-8">Checkout</h1>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Form Section */}
            <div className="lg:col-span-2">
              <div className="bg-card border border-border rounded-xl p-6">
                <h2 className="text-lg font-semibold mb-6">Datos de Envío</h2>
                <CheckoutForm
                  onSubmit={handleSubmit}
                  isSubmitting={isSubmitting}
                  appliedDiscount={appliedDiscount}
                  onApplyDiscount={setAppliedDiscount}
                />
              </div>
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <OrderSummary appliedDiscount={appliedDiscount} />
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
